import React, { useEffect, useRef, useState, useMemo } from 'react';
import { fileUpload, getFileUrl } from '@/services/CommonService';
import { updateOfferService } from '@/services/OfferServices';
import validationErrors from '@/lib/validationErrors';
import { useDispatch } from 'react-redux';
import ToastMe from '@/components/ui/ToastMe';

const ImagesTab = ({ dataSource, onImageUpdated }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [fileName, setFileName] = useState(dataSource?.file_name || '');
  const [cacheKey, setCacheKey] = useState(Date.now()); // bust cache after upload
  const [imgError, setImgError] = useState(false);

  // If parent gives new dataSource, sync local preview
  useEffect(() => {
    setFileName(dataSource?.file_name || '');
    setImgError(false);
    setCacheKey(Date.now());
  }, [dataSource?._id, dataSource?.file_name]);

  const imgSrc = useMemo(() => {
    if (!fileName) return '';
    // If your backend serves /api/file/get/:folder/:name, getFileUrl will build that.
    // If you added static /uploads, you can swap to `/uploads/offer/${fileName}` instead.
    return `${getFileUrl(fileName, 'offer')}?v=${cacheKey}`;
  }, [fileName, cacheKey]);

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) {
      ToastMe('File is not valid', 'error');
      return;
    }
    if (!file.type?.includes('image/')) {
      ToastMe('Please upload only image files', 'error');
      return;
    }

    setIsLoading(true);
    dispatch(fileUpload({ file }, 'offer'))
      .then((res) => {
        const uploaded = res?.data;
        const newName = uploaded?.fileName;
        if (!newName) {
          ToastMe('Upload failed: missing fileName in response', 'error');
          setIsLoading(false);
          return;
        }

        // Persist it on the Offer
        return dispatch(
          updateOfferService(dataSource?._id, { _id: dataSource?._id, file_name: newName })
        ).then(() => {
          // Update local preview immediately
          setFileName(newName);
          setCacheKey(Date.now());
          setImgError(false);

          // Clear file input so user can re-select same file if needed
          if (fileInputRef.current) fileInputRef.current.value = '';

          // Let parent refresh if it wants to
          if (typeof onImageUpdated === 'function') onImageUpdated(newName);

          ToastMe('Image updated', 'success');
        });
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="db-image-wrap">
        {imgSrc ? (
          <img
            className="db-image"
            alt="offer"
            src={imgSrc}
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-[182px] bg-gray-100 flex items-center justify-center text-gray-400">
            No image
          </div>
        )}
        {imgError && (
          <div className="mt-2 text-sm text-red-500">
            Couldn’t load image. Make sure the server serves it at <code>/api/file/get/offer/{fileName}</code> or <code>/uploads/offer/{fileName}</code>.
          </div>
        )}
      </div>

      <form onSubmit={(e) => e.preventDefault()}>
        <p className="mt-2">Recommended size: 262×182</p>
        <div className="flex gap-3 md:gap-4 py-4">
          <label
            htmlFor="photo"
            className={`db-btn relative cursor-pointer h-[38px] shadow-[0px_6px_10px_rgba(23,_114,_255,_0.24)] text-white ${
              isLoading ? 'bg-gray-400 pointer-events-none' : 'bg-primary'
            }`}
          >
            <i className="lab lab-upload-image" />
            <span className="hidden sm:inline-block ml-1">
              {isLoading ? 'Uploading…' : 'Upload New Image'}
            </span>
            <input
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept="image/png, image/jpeg, image/jpg, image/gif"
              type="file"
              id="photo"
              className="absolute top-0 left-0 w-full h-full -z-10 opacity-0"
              disabled={isLoading}
            />
          </label>
        </div>
      </form>
    </div>
  );
};

export default ImagesTab;
